import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { modules } from '../../../registry'
import { useEffectivePermissions } from '../../../../auth/useEffectivePermissions'

// ── KostenstellenSubNav ───────────────────────────────────────────────────────
// Module-local left rail. Kostenstellen owns NO master data — it's a
// read-only result viewer. Labels resolve via the `kostenstellen` i18n
// namespace so a language flip applies without a remount.

interface SubNavItem {
  i18nKey: string
  icon: string
  path: string
  /** Exact-match only. Set on leaf paths that must NOT prefix-match. The non-end
   *  items (imports, auswertung) intentionally stay active on their sub-routes
   *  (e.g. /imports/ass, /auswertung/schule/:id). */
  end?: boolean
}

// NOTE: Übersicht points at the real /uebersicht route — the bare
// /verwaltung/kostenstellen path redirects there (KostenstellenPage), so a
// NavLink to the bare path with `end` would never match the redirected URL.
// Kiosk was removed: its route only redirects to PCA (DSFinV-K out of scope).
const NAV_ITEMS: SubNavItem[] = [
  { i18nKey: 'subnav.uebersicht',  icon: 'map',            path: '/verwaltung/kostenstellen/uebersicht', end: true },
  { i18nKey: 'subnav.imports',     icon: 'cloud_upload',   path: '/verwaltung/kostenstellen/imports' },
  { i18nKey: 'subnav.stammdaten',   icon: 'account_tree',  path: '/verwaltung/kostenstellen/stammdaten' },
  { i18nKey: 'subnav.preiskatalog', icon: 'price_check',   path: '/verwaltung/kostenstellen/preiskatalog' },
  { i18nKey: 'subnav.bioQuote',     icon: 'eco',           path: '/verwaltung/kostenstellen/bio-quote' },
  { i18nKey: 'subnav.auswertung',   icon: 'bar_chart',     path: '/verwaltung/kostenstellen/auswertung' },
  { i18nKey: 'subnav.pca',         icon: 'point_of_sale',  path: '/verwaltung/kostenstellen/pca' },
  { i18nKey: 'subnav.sapKasse',    icon: 'storefront',     path: '/verwaltung/kostenstellen/sap-kasse', end: true },
  { i18nKey: 'subnav.lieferanten', icon: 'local_shipping', path: '/verwaltung/kostenstellen/lieferanten' },
  { i18nKey: 'subnav.refood',      icon: 'recycling',      path: '/verwaltung/kostenstellen/refood' },
]

/** Registry module for a sub-nav path (same lookup the Sidebar uses) — undefined = not gated. */
const moduleIdForPath = (path: string) => modules.find(m => m.path === path)?.id

export default function KostenstellenSubNav() {
  const { t } = useTranslation('kostenstellen')
  // Same source of truth as the Sidebar and RoleGuard: ops.RoleModuleAccess via /api/me/permissions.
  // While permissions load, gated items stay hidden (no flash of a rail the user may not have).
  const { canRead, isLoading } = useEffectivePermissions()
  const visible = NAV_ITEMS.filter(item => {
    const id = moduleIdForPath(item.path)
    return id ? (!isLoading && canRead(id)) : true
  })
  return (
    <nav
      className="flex-shrink-0 w-[200px] bg-transparent border-r border-outline-variant/30 flex flex-col py-3 overflow-y-auto"
      aria-label={t('subnav.ariaLabel')}
    >
      {visible.map(item => (
        <NavLink
          key={item.path}
          to={item.path}
          end={!!item.end}
          className={({ isActive }) =>
            `relative flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors select-none ${
              isActive
                ? 'text-on-surface font-semibold bg-white/55 dark:bg-slate-900/40 backdrop-blur-md'
                : 'text-on-surface-variant font-medium hover:bg-white/35 dark:hover:bg-slate-900/25 hover:backdrop-blur-md'
            }`
          }
        >
          {({ isActive }) => (
            <>
              {isActive && (
                <span
                  className="absolute left-0 top-1.5 bottom-1.5 w-[3px] bg-primary rounded-r-full"
                  aria-hidden="true"
                />
              )}
              <span
                className="material-symbols-outlined flex-shrink-0"
                style={{ fontSize: 18 }}
                aria-hidden="true"
              >
                {item.icon}
              </span>
              <span className="truncate">{t(item.i18nKey)}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  )
}
